'use client'

import type { WagmiAdapter } from '@reown/appkit-adapter-wagmi'
import type { AppKitNetwork } from '@reown/appkit/networks'
import type { QueryClient } from '@tanstack/react-query'

import { BlockchainApiController } from '@reown/appkit-controllers'
import { createAppKit, useAppKitTheme } from '@reown/appkit/react'
import { useIsMounted } from '@repo/hooks'
import { type PropsWithChildren, useEffect } from 'react'
import { cookieToInitialState, type Config as WagmiConfig, WagmiProvider } from 'wagmi'

import { ReactQueryProvider } from './ReactQueryProvider'
import { UserAccountProvider } from './UserAccountProvider'

type CreateAppKitOptions = Parameters<typeof createAppKit>[0]

// AppKit fetches ENS identity on connect and renders the avatar URL as an
// <img> in the modal. The URL can point at arbitrary hosts (IPFS gateways,
// NFT media), which CSP blocks. Strip avatar so the modal uses its gradient.
const _originalFetchIdentity = BlockchainApiController.fetchIdentity
BlockchainApiController.fetchIdentity = async request => {
  const identity = await _originalFetchIdentity.call(BlockchainApiController, request)
  return { ...identity, avatar: null }
}

type ThemeMode = 'light' | 'dark' | 'system'

function resolveThemeFromDOM(themeMode: ThemeMode): 'light' | 'dark' {
  const root = document.documentElement
  if (root.classList.contains('dark')) return 'dark'
  if (root.classList.contains('light')) return 'light'
  // No theme class on <html> (an app that never stamps one). Fall back per
  // `themeMode`: 'system' follows the OS preference; otherwise use the fixed
  // value — the default 'light' preserves the historical classless behavior so
  // adopting this provider never flips a light-only app to dark on a dark OS.
  if (themeMode === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
  }
  return themeMode
}

function AppKitThemeSync({ themeMode }: { themeMode: ThemeMode }) {
  const { setThemeMode } = useAppKitTheme()

  useEffect(() => {
    setThemeMode(resolveThemeFromDOM(themeMode))

    // Explicit toggles flip the class on <html>.
    const observer = new MutationObserver(() => {
      setThemeMode(resolveThemeFromDOM(themeMode))
    })
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })

    // Only 'system' mode resolves from the OS preference, so only it needs to
    // react to OS theme changes; class-driven apps flow through the observer above.
    let mediaQuery: MediaQueryList | undefined
    const onMediaChange = () => setThemeMode(resolveThemeFromDOM(themeMode))
    if (themeMode === 'system') {
      mediaQuery = window.matchMedia('(prefers-color-scheme: dark)')
      mediaQuery.addEventListener('change', onMediaChange)
    }

    return () => {
      observer.disconnect()
      mediaQuery?.removeEventListener('change', onMediaChange)
    }
  }, [setThemeMode, themeMode])

  return null
}
export function Web3Provider({
  children,
  wagmiAdapter,
  wagmiConfig,
  cookies,
  disableAppKit = false,
  ssr = false,
  enableAppKitAnalytics,
  metadata,
  featuredWalletIds,
  screenAddressEndpoint,
  queryClient,
  themeMode = 'light'
}: PropsWithChildren<{
  wagmiAdapter: WagmiAdapter
  wagmiConfig: WagmiConfig
  cookies: string | null
  disableAppKit?: boolean
  /**
   * Render children during SSR instead of blanking the tree until client
   * mount. The wagmi config already runs with `ssr: true` + cookie-derived
   * initial state, so the page content server-renders consistently; AppKit is
   * still only initialized in the browser (first client render, before
   * children hydrate, so EIP-6963 wallet detection keeps working). Children
   * must not call AppKit hooks that require `createAppKit` on the server —
   * use {@link useAppKitSSR} instead of `useAppKit`.
   *
   * Opt-in: apps that haven't audited their tree for SSR-unsafe AppKit hook
   * usage keep the legacy mount gate.
   */
  ssr?: boolean
  /**
   * Gates AppKit's built-in analytics (`features.analytics`). When omitted, no
   * `analytics` key is set and AppKit falls back to the Reown Cloud project
   * configuration (current behavior for all consumers). When provided, it is
   * applied at init only — AppKit has no runtime analytics toggle.
   */
  enableAppKitAnalytics?: boolean
  /**
   * dApp identity AppKit self-reports to wallets over WalletConnect (name,
   * description, url, icons). When omitted, AppKit derives it from the page
   * (`document.title` + favicon), so each app should pass its own metadata to
   * control how it appears in the wallet's connection/confirmation UI. Note:
   * some wallets (e.g. Rainbow) prefer their own registry over this metadata.
   */
  metadata?: CreateAppKitOptions['metadata']
  /**
   * Wallet ids AppKit pins to the top of the connect modal (Reown explorer
   * ids). When omitted, AppKit uses its default ordering. Lets a consumer keep
   * its own featured wallets when adopting the shared provider.
   */
  featuredWalletIds?: CreateAppKitOptions['featuredWalletIds']
  /**
   * Same-origin route the connect-time screening queries — pass
   * `SCREEN_ADDRESS_ROUTE` from `@repo/address-screening/client` unless the app
   * mounts its route elsewhere (e.g. under a Next `basePath`). Screening
   * delegates to the shared `@repo/address-screening` client (POST
   * `{ address }`, **fail-closed**) and runs in every environment except E2E.
   */
  screenAddressEndpoint: string
  /**
   * Existing react-query client to reuse instead of the package default. Pass
   * this when the consumer already mounts a tuned client above this provider
   * (e.g. vvrm's `getQueryClient()` singleton) so the app tree below doesn't
   * split into a second, shadowing cache. Omitted → the package's own client.
   */
  queryClient?: QueryClient
  /**
   * How the AppKit modal resolves its theme when `<html>` carries no `dark`/
   * `light` class. `'system'` follows the OS preference (and tracks
   * `prefers-color-scheme` changes); `'light'`/`'dark'` pin a fixed value.
   * Defaults to `'light'` so a light-only app that never stamps a theme class
   * (no `ThemeProvider`) keeps its modal light on a dark-OS machine. Apps that
   * stamp a class (e.g. via next-themes) drive the theme through the class
   * regardless of this value; pass `'system'` to opt a classless app into OS
   * following (vvrm's "system" mode).
   */
  themeMode?: ThemeMode
}>) {
  const initialState = cookieToInitialState(wagmiConfig, cookies)

  const isMounted = useIsMounted()

  // Legacy (non-ssr) behavior: fixes hydration error when using AppKit; if
  // removed, AppKit doesn't detect available browser wallets.
  //
  // Timing, not just "render twice": `useIsMounted` must flip AFTER the first
  // commit. A `useSyncExternalStore` variant reading `true` on the first client
  // render satisfies hydration but drops every EIP-6963 wallet from the modal
  // (this has regressed in production before). The mechanism is downstream and is NOT "announce
  // before createAppKit" — wagmi builds mipd in `createConfig` above this, and
  // under `ssr: true` folds those connectors in at `hydrate().onMount()`. So
  // don't relax this on a mechanism argument; the guard is
  // apps/vvrm-app/test/misc/walletDiscovery.spec.ts.
  if (!ssr && !isMounted) return null

  if (!disableAppKit && typeof window !== 'undefined') {
    // Our chain configs carry a `custom.contracts` field whose `startBlock`
    // values are BigInts. AppKit doesn't read `chain.custom`, but spreads the
    // chain object verbatim into analytics events / connection storage, where
    // a plain `JSON.stringify` throws `TypeError: Do not know how to serialize
    // a BigInt`. Pass a clone with `custom` stripped — leaves
    // `wagmiConfig.chains` (the source of truth for `client.chain`) untouched.
    const sanitizedChains = wagmiAdapter.wagmiConfig.chains.map(chain => {
      const { custom: _custom, ...rest } = chain // oxlint-disable-line typescript/no-unused-vars
      return rest
    })

    createAppKit({
      adapters: [wagmiAdapter],
      projectId: wagmiAdapter.projectId ?? '',
      networks: sanitizedChains as unknown as [AppKitNetwork, ...AppKitNetwork[]],
      defaultNetwork: sanitizedChains[0],
      ...(metadata !== undefined ? { metadata } : {}),
      ...(featuredWalletIds !== undefined ? { featuredWalletIds } : {}),
      enableWalletGuide: false,
      allowUnsupportedChain: true, // This is needed to allow switching to unsupported chains (e.g. Sepolia in Rabby). Otherwise, AppKit fails silently.
      // A custom font family makes AppKit skip injecting its remote KHTeka fonts and
      // their preload tags, which warned "preloaded but not used" wherever the modal
      // never opens. FK Grotesk's document-scoped @font-face resolves in its shadow DOM.
      themeVariables: {
        '--w3m-font-family': "'FK Grotesk', ui-sans-serif, system-ui, sans-serif"
      },
      features: {
        email: false,
        socials: false,
        legalCheckbox: true,
        swaps: false,
        onramp: false,
        send: false,
        ...(enableAppKitAnalytics !== undefined ? { analytics: enableAppKitAnalytics } : {})
      }
    })
  }

  return (
    <ReactQueryProvider client={queryClient}>
      <WagmiProvider config={wagmiConfig} initialState={initialState}>
        <UserAccountProvider screenAddressEndpoint={screenAddressEndpoint}>
          {/* useAppKitTheme throws before createAppKit runs, so keep this off
              the server render and the first hydration pass. */}
          {!disableAppKit && isMounted && <AppKitThemeSync themeMode={themeMode} />}
          {children}
        </UserAccountProvider>
      </WagmiProvider>
    </ReactQueryProvider>
  )
}
